import React, { useState, useEffect } from 'react';
import { CaptureItem, Video } from '../types';
import { formatDate, buildBreadcrumbs, BreadcrumbItem } from '../utils';
import { STATUS_STYLES } from '../types';
import IconButton from '../../../shared/components/ui/IconButton';
import { videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';

interface FileExplorerProps {
  folders: CaptureItem[];
  files: CaptureItem[];
  videosByFolder: Record<string, Video>;
  uploadingFiles: Map<string, { name: string; progress: number; status: 'uploading' | 'processing' | 'completed' | 'error' }>;
  selectedFolderPath: string;
  globalSearch: string;
  isLoading: boolean;
  stuckVideoIds?: Set<string>;
  stuckVideos?: Video[];
  onFolderClick: (folder: CaptureItem) => void;
  onBreadcrumbClick: (crumb: BreadcrumbItem) => void;
  onRefresh: () => void;
  onViewVideo: (videoId: string) => void;
  onViewAsset?: (item: CaptureItem) => void;
  onRowClickVideo?: (videoId: string) => void;
  onRowClickAsset?: (item: CaptureItem) => void;
  onOpenStorageLink?: (item: CaptureItem) => void;
  onEditVideo?: (videoId: string) => void;
  onDeleteVideo?: (videoId: string, videoName: string, storagePath: string) => void;
  onDeleteHighlight?: (highlightId: string, highlightName: string) => void;
  onDeleteTactics?: (tacticsId: string, tacticsName: string) => void;
  onEditHighlight?: (highlightId: string) => void;
  customBreadcrumbs?: BreadcrumbItem[];
}

const FileExplorer: React.FC<FileExplorerProps> = ({
  folders,
  files,
  videosByFolder,
  uploadingFiles,
  selectedFolderPath,
  globalSearch,
  isLoading,
  stuckVideoIds = new Set(),
  stuckVideos = [],
  onFolderClick,
  onBreadcrumbClick,
  onRefresh,
  onViewVideo,
  onViewAsset,
  onRowClickVideo,
  onRowClickAsset,
  onOpenStorageLink,
  onEditVideo,
  onDeleteVideo,
  onDeleteHighlight,
  onDeleteTactics,
  onEditHighlight,
  customBreadcrumbs,
}) => {
  const breadcrumbs = customBreadcrumbs || buildBreadcrumbs(selectedFolderPath);
  const rootFolderKey = selectedFolderPath ? selectedFolderPath.split('/')[0] : selectedFolderPath;
  const activeVideo = rootFolderKey ? videosByFolder[rootFolderKey] : undefined;
  
  // Track processing state and existing jobs for videos
  const [processingVideoIds, setProcessingVideoIds] = useState<Set<string>>(new Set());
  const [videoJobsMap, setVideoJobsMap] = useState<Record<string, boolean>>({}); // videoId -> hasTranscodeMasterJob

  // Load jobs for videos that might need processing
  useEffect(() => {
    const loadJobsForVideos = async () => {
      const videoIds = files
        .filter(f => f.type === 'file' && f.videoId && f.video?.status === 'uploaded')
        .map(f => f.videoId!)
        .filter(Boolean);

      if (videoIds.length === 0) {
        setVideoJobsMap({});
        return;
      }

      const jobsMap: Record<string, boolean> = {};
      
      for (const videoId of videoIds) {
        const result = await videoProcessingJobsService.getJobsByVideoId(videoId);
        if (result.success && result.data) {
          const hasTranscodeMaster = result.data.some(
            job => job.job_type === 'transcode_master' && job.is_active
          );
          jobsMap[videoId] = hasTranscodeMaster;
        } else {
          jobsMap[videoId] = false;
        }
      }

      setVideoJobsMap(jobsMap);
    };

    loadJobsForVideos();
  }, [files]);

  const handleProcessVideo = async (videoId: string, item: CaptureItem) => {
    if (!videoId) return;

    setProcessingVideoIds(prev => new Set([...prev, videoId]));

    try {
      const result = await videoProcessingJobsService.processVideo(videoId);
      
      if (result.success) {
        // Update jobs map
        setVideoJobsMap(prev => ({ ...prev, [videoId]: true }));
        // Refresh folder contents
        onRefresh();
      } else {
        alert(result.error || 'Failed to start video processing');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start video processing');
    } finally {
      setProcessingVideoIds(prev => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
    }
  };

  const canProcessVideo = (item: CaptureItem): boolean => {
    if (!item.videoId || !item.video) return false;
    
    // Must have storage_path (file uploaded)
    if (!item.video.storage_path) return false;
    
    // Status must be 'uploaded'
    if (item.video.status !== 'uploaded') return false;
    
    // No existing active transcode_master job
    if (videoJobsMap[item.videoId]) return false;
    
    return true;
  };

  // Check if video was uploaded via remote link (has source_url in processing_notes)
  const isLinkUpload = (item: CaptureItem): boolean => {
    if (!item.video) return false;
    const processingNotes = item.video.processing_notes as Record<string, any> | null;
    return !!processingNotes?.source_url;
  };
  const getBreadcrumbLabel = (crumb: BreadcrumbItem) => {
    if (crumb.id === 'root') {
      return 'All Files';
    }
    // If custom breadcrumbs are provided, use the name directly (it's already resolved)
    if (customBreadcrumbs) {
      return crumb.name;
    }
    // Otherwise, use the original logic for Video Library
    if (crumb.path && crumb.path.includes('/')) {
      const assetName = crumb.path.split('/').pop();
      if (assetName) {
        return assetName;
      }
    }
    const lookupKey = crumb.path ? crumb.path.split('/')[0] : crumb.id;
    return (lookupKey && videosByFolder[lookupKey]?.file_name) || crumb.name;
  };

  const EyeIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

  const RefreshIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.93 4.93a10 10 0 0114.14 0l.35.36V3a1 1 0 012 0v6a1 1 0 01-1 1h-6a1 1 0 010-2h3.59l-.35-.36a8 8 0 00-11.32 0 1 1 0 11-1.41-1.41zM19.07 19.07a10 10 0 01-14.14 0l-.35-.36V21a1 1 0 01-2 0v-6a1 1 0 011-1h6a1 1 0 010 2H6.59l.35.36a8 8 0 0011.32 0 1 1 0 111.41 1.41z" />
    </svg>
  );

  const EditIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
      />
    </svg>
  );

  // Combine folders, files, and uploading files
  const allItems = [...folders, ...files];
  const uploadingItems: CaptureItem[] = Array.from(uploadingFiles.entries()).map(([id, upload]) => ({
    id: `upload-${id}`,
    name: upload.name,
    type: 'file' as const,
    status: upload.status === 'completed' ? 'ready' : upload.status === 'error' ? 'error' : 'processing',
    updatedAt: new Date().toISOString(),
  }));
  const combined = [...allItems, ...uploadingItems];
  
  // Filter by global search (client-side)
  const currentItems = !globalSearch.trim()
    ? combined
    : combined.filter((item) => item.name.toLowerCase().includes(globalSearch.toLowerCase()));

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              <button
                onClick={() => onBreadcrumbClick(crumb)}
                className={`text-sm ${
                  ((crumb.id === 'root' && !selectedFolderPath) || crumb.path === selectedFolderPath)
                    ? 'text-blue-600 font-medium'
                    : 'hover:text-blue-600'
                }`}
              >
                {getBreadcrumbLabel(crumb)}
              </button>
              {index < breadcrumbs.length - 1 && <span>/</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {stuckVideoIds.size > 0 && (
            <div className="relative group">
              <div className="flex items-center gap-2 px-2 py-1 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded cursor-help">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{stuckVideoIds.size} still processing</span>
              </div>
              {/* Tooltip */}
              <div className="absolute right-0 top-full mt-1 z-50 hidden group-hover:block">
                <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg min-w-[200px] max-w-[300px]">
                  <div className="font-medium mb-1.5">Videos Still Processing:</div>
                  <div className="space-y-1">
                    {stuckVideos.map((video) => (
                      <div key={video.id} className="truncate">
                        {video.file_name}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-700 text-gray-400 text-xs">
                    Edit video to set inactive
                  </div>
                </div>
              </div>
            </div>
          )}
          <IconButton
            onClick={onRefresh}
            variant="ghost"
            size="sm"
            className="border border-gray-200 text-gray-600 hover:text-gray-900 rounded-full bg-white"
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>
        </div>
      </div>

      <div className="p-4 relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}
          {currentItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg space-y-2">
              {activeVideo ? (
                <>
                  <p className="font-medium text-gray-800">
                    {activeVideo.file_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {activeVideo.status === 'processing'
                      ? 'We are processing this video. Thumbnails and proxies will appear automatically once done.'
                      : activeVideo.status === 'ready'
                      ? 'Video is ready. Assets are stored in Cloudflare R2 and will appear here soon.'
                      : 'No assets available in this folder yet.'}
                  </p>
                  <p className="text-xs text-gray-400">
                    Status: {activeVideo.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-gray-700 mb-1">
                    {globalSearch.trim() ? 'No files match your search' : 'This folder is empty'}
                  </p>
                  <p className="text-sm">
                    {globalSearch.trim() ? 'Try a different search term' : 'Upload a new video or drag files here to get started.'}
                  </p>
                </>
              )}
            </div>
          ) : (
            <div>
              {/* Folders: Simple list without table */}
              {currentItems.filter(item => item.type === 'folder').length > 0 && (
                <div className="space-y-1 mb-4">
                      {currentItems
                        .filter(item => item.type === 'folder' && item.name !== 'temp')
                    .map((item) => {
                      // Check if this folder has a stuck video
                      const folderVideo = item.video;
                      const isStuck = folderVideo && stuckVideoIds.has(folderVideo.id);
                      
                      return (
                        <div
                          key={item.id}
                          onClick={() => onFolderClick(item)}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <span>📁</span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900">
                                {item.name}
                              </p>
                              {item.video && (
                                <span
                                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                    isLinkUpload(item)
                                      ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                      : 'bg-gray-100 text-gray-700 border border-gray-200'
                                  }`}
                                  title={
                                    isLinkUpload(item)
                                      ? 'Uploaded via remote link'
                                      : 'Uploaded from local file'
                                  }
                                >
                                  {isLinkUpload(item) ? (
                                    <span className="flex items-center gap-1">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                      </svg>
                                      Link
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                      </svg>
                                      Local
                                    </span>
                                  )}
                                </span>
                              )}
                              {isStuck && (
                                <span className="px-1.5 py-0.5 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Still processing
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-xs text-gray-500">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Files: Table with columns */}
              {currentItems.filter(item => item.type === 'file').length > 0 && (
                <div>
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2 text-left">File</th>
                        <th className="px-3 py-2 text-left">Updated At</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Size</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm">
                      {currentItems
                        .filter(item => item.type === 'file')
                        .map((item) => (
                          <tr 
                            key={item.id} 
                            className="hover:bg-gray-50"
                            onClick={() => {
                              // Navigate to detail page on row click
                              if (item.videoId && onRowClickVideo) {
                                onRowClickVideo(item.videoId);
                              } else if ((item.id.startsWith('highlight-') || item.id.startsWith('tactics-')) && onRowClickAsset) {
                                onRowClickAsset(item);
                              }
                            }}
                            style={(item.videoId || item.id.startsWith('highlight-') || item.id.startsWith('tactics-')) ? { cursor: 'pointer' } : {}}
                          >
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-3">
                                <span>🎬</span>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-gray-900 font-medium">{item.name}</p>
                                    {item.video && (
                                      <span
                                        className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                          isLinkUpload(item)
                                            ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                                        }`}
                                        title={
                                          isLinkUpload(item)
                                            ? 'Uploaded via remote link'
                                            : 'Uploaded from local file'
                                        }
                                      >
                                        {isLinkUpload(item) ? (
                                          <span className="flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                            </svg>
                                            Link
                                          </span>
                                        ) : (
                                          <span className="flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            Local
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <p className="text-xs text-gray-500 max-w-xs truncate mt-1" title={item.description}>
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-gray-500">
                              {item.updatedAt ? formatDate(item.updatedAt) : '—'}
                            </td>
                            <td className="px-3 py-3">
                              {item.status ? (
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded-full text-xs ${STATUS_STYLES[item.status]}`}>
                                    {item.status === 'ready'
                                      ? 'Ready'
                                      : item.status === 'processing'
                                      ? 'Processing'
                                      : item.status === 'pending_upload'
                                      ? 'Pending upload'
                                      : item.status === 'uploaded'
                                      ? 'Uploaded'
                                      : 'Error'}
                                  </span>
                                  {item.id.startsWith('upload-') && uploadingFiles.has(item.id.replace('upload-', '')) && (
                                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-blue-600 transition-all duration-300"
                                        style={{
                                          width: `${uploadingFiles.get(item.id.replace('upload-', ''))?.progress || 0}%`,
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-3 py-3 text-gray-500">{item.size || '—'}</td>
                            <td className="px-3 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {/* For highlights and tactics - always show actions if onViewAsset exists */}
                                {((item.id.startsWith('highlight-') || item.id.startsWith('tactics-')) && onViewAsset) ? (
                                  <>
                                    {item.storagePath && onOpenStorageLink && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Open storage link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onOpenStorageLink(item);
                                        }}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </IconButton>
                                    )}
                                    {item.storagePath && !onOpenStorageLink && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Open storage link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const cdnUrl = `https://cdn.capture.kridah.com/${item.storagePath}`;
                                          window.open(cdnUrl, '_blank');
                                        }}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </IconButton>
                                    )}
                                    <IconButton
                                      variant="primary"
                                      size="sm"
                                      title="View details"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onViewAsset(item);
                                      }}
                                    >
                                      <EyeIcon />
                                    </IconButton>
                                    {/* Edit button for highlights - show for all active highlights */}
                                    {item.id.startsWith('highlight-') && onEditHighlight && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Edit highlight"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const highlightId = item.id.replace('highlight-', '');
                                          onEditHighlight(highlightId);
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    )}
                                    {/* Delete button for highlights - only show if has storage path */}
                                    {item.id.startsWith('highlight-') && onDeleteHighlight && item.storagePath && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Delete highlight"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const highlightId = item.id.replace('highlight-', '');
                                          onDeleteHighlight(highlightId, item.name);
                                        }}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </IconButton>
                                    )}
                                    {/* Delete button for tactics - only show if has storage path */}
                                    {item.id.startsWith('tactics-') && onDeleteTactics && item.storagePath && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Delete tactics image"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const tacticsId = item.id.replace('tactics-', '');
                                          onDeleteTactics(tacticsId, item.name);
                                        }}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </IconButton>
                                    )}
                                  </>
                                ) : (item.assetKind && item.assetKind !== 'master' && onViewAsset) || 
                                 (item.storagePath && onViewAsset && !item.videoId) ? (
                                  <>
                                    {item.storagePath && onOpenStorageLink && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Open storage link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onOpenStorageLink(item);
                                        }}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </IconButton>
                                    )}
                                    {item.storagePath && !onOpenStorageLink && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Open storage link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const cdnUrl = `https://cdn.capture.kridah.com/${item.storagePath}`;
                                          window.open(cdnUrl, '_blank');
                                        }}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </IconButton>
                                    )}
                                    <IconButton
                                      variant="primary"
                                      size="sm"
                                      title="View details"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onViewAsset(item);
                                      }}
                                    >
                                      <EyeIcon />
                                    </IconButton>
                                  </>
                                ) : (
                                  item.videoId && (
                                  <>
                                    {/* Process Video button - only show if conditions are met */}
                                    {canProcessVideo(item) && (
                                      <IconButton
                                        variant="primary"
                                        size="sm"
                                        title="Process video"
                                        disabled={processingVideoIds.has(item.videoId)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleProcessVideo(item.videoId!, item);
                                        }}
                                      >
                                        {processingVideoIds.has(item.videoId) ? (
                                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                        ) : (
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                        )}
                                      </IconButton>
                                    )}
                                    {/* Edit button for videos - show for all active videos */}
                                    {onEditVideo && item.assetKind === 'master' && item.video?.is_active !== false && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Edit metadata"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEditVideo(item.videoId!);
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    )}
                                    {item.storagePath && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Open storage link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const cdnUrl = `https://cdn.capture.kridah.com/${item.storagePath}`;
                                          window.open(cdnUrl, '_blank');
                                        }}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </IconButton>
                                    )}
                                    <IconButton
                                      variant="primary"
                                      size="sm"
                                      title="View details"
                                      onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                      ) => {
                                        e.stopPropagation();
                                        onViewVideo(item.videoId!);
                                      }}
                                    >
                                      <EyeIcon />
                                    </IconButton>
                                    {/* Delete button for videos (master files) */}
                                    {onDeleteVideo && item.assetKind === 'master' && item.videoId && item.storagePath && (
                                      <IconButton
                                        variant="ghost"
                                        size="sm"
                                        title="Delete video"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDeleteVideo(item.videoId!, item.name, item.storagePath!);
                                        }}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </IconButton>
                                    )}
                                  </>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default FileExplorer;

